import type { IFilters } from '@/components/filters';
import Filters, { CurrentPageEnum } from '@/components/filters';
import { Pagination, Slider } from 'antd';
import {
  Checkbox,
  Dropdown,
  Label,
  Spinner,
  Table,
  TextInput,
} from 'flowbite-react';
import { Fragment, useEffect, useMemo, useState, type FC } from 'react';
import {
  HiChevronDoubleDown,
  HiChevronDoubleUp,
  HiOutlineSelector,
} from 'react-icons/hi';
import Good from './good';
import type { IReportGood } from './types';

import HoverTooltip from '@/components/hover-tooltip';
import getWbImageUrl from '@/helpers/get-wb-image-url';
import { Marketplace } from '@/store/organization/projectSlice';

import { fetchNinjaCsrf, getCachedNinjaCsrf } from '@/libs/ninja/csrf';
import { BACKEND_SERVER_NINJA } from '@/settings';
import axios from 'axios';
import useSWR from 'swr';
import LocalFilter from './local-filter';

const getImageUrl = (
  platform: Marketplace | undefined,
  productId: string | number,
  imageLink: string
): string => {
  if (platform === Marketplace.Wildberries) {
    return getWbImageUrl(+productId);
  }
  if (platform === Marketplace.YandexMarket) {
    return `${imageLink}${
      imageLink.endsWith('/9hq_multiply') ? '' : '/9hq_multiply'
    }`;
  }
  return imageLink;
};

const getLinkUrl = (
  platform: Marketplace | undefined,
  link: string
): string => {
  if (platform === Marketplace.YandexMarket) {
    return link.replace('product--/', 'product/');
  }
  return link;
};

const ReportGoods: FC = () => {
  const [filters, setFilters] = useState<null | IFilters>(null);
  const [selectedProductId, setSelectedProductId] = useState<
    string | number | null
  >(null);

  const [checkboxesMap, setCheckboxesMap] = useState<{
    [key: string]: boolean;
  }>({});

  const [brandsList, setBrandsList] = useState<string[]>([]);
  const [sellersList, setSellersList] = useState<string[]>([]);
  const [badgesList, setBadgesList] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 0]);
  const [isHavePrice, setIsHavePrice] = useState(false);
  const [minPriceRange, setMinPriceRange] = useState(0);
  const [maxPriceRange, setMaxPriceRange] = useState(0);

  const { data: goods, isLoading } = useSWR(
    filters
      ? () => {
          return [
            BACKEND_SERVER_NINJA +
              '/reports/top_products/top_products/report_top_products',
            filters,
          ];
        }
      : null,
    async ([url, body]) => {
      const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
      if (!url || !body) return [];

      return await axios
        .post<{ data: { products: IReportGood[] } }>(url, body, {
          withCredentials: true,
          headers: { 'X-CSRFTOKEN': csrf },
        })
        .then((res) => res?.data?.data?.products ?? []);
    }
  );

  enum SortConfigTypeEnum {
    disabled = 'disabled',
    asc = 'asc',
    desc = 'desc',
  }

  const sortHeaders: {
    [key in keyof Partial<IReportGood>]: { text: string; tooltip?: string };
  } = {
    impressions: { text: 'Показы' },
    share: { text: 'Доля' },
    position: { text: 'Ср. Поз.' },
    price: {
      text: 'Цена',
      tooltip: 'Актуальная цена на конец отчетного периода',
    },
    rating: { text: 'Рейтинг' },
    rating_count: { text: 'Отзывы' },
    badges: { text: 'Шильдики' },
    stock: { text: 'Наличие' },
  } as const;

  const defaultSortConfig = Object.keys(sortHeaders).reduce(
    (acc, value) => ({ ...acc, [value]: SortConfigTypeEnum.disabled }),
    {}
  );

  const [currentPage, setCurrentPage] = useState(1);

  const [sortConfig, setSortConfig] = useState<{
    [key: string]: SortConfigTypeEnum;
  }>(defaultSortConfig);

  const uniqueBrands = [
    ...new Set((goods || []).map((good) => good.brand)),
  ].map((value) => ({
    name: value,
    length: (goods || []).filter((good) => good.brand === value).length,
  }));

  const uniqueSellers = [
    ...new Set((goods || []).map((good) => good.seller)),
  ].map((value) => ({
    name: value,
    length: (goods || []).filter((good) => good.seller === value).length,
  }));

  // Создаем массив уникальных шильдиков
  const uniqueBadges = [
    ...new Set((goods || []).flatMap((good) => good.badges)),
  ].map((value) => ({
    name: value,
    length:
      (goods || []).filter((good) => good?.badges?.includes?.(value))?.length ??
      0,
  }));

  // Initialize brands and sellers lists when goods data is loaded
  useEffect(() => {
    if (goods && goods.length > 0) {
      // Initialize with all brands and sellers sorted alphabetically
      setBrandsList(
        uniqueBrands
          .map(({ name }) => name)
          .sort((a, b) => a?.localeCompare?.(b) ?? 0)
      );
      setSellersList(
        uniqueSellers
          .map(({ name }) => name)
          .sort((a, b) => a?.localeCompare?.(b) ?? 0)
      );
      setBadgesList(
        uniqueBadges
          .map(({ name }) => name)
          .sort((a, b) => a?.localeCompare?.(b) ?? 0)
      );
    }
  }, [goods]);

  const sortedGoods = useMemo(
    () =>
      [...(goods || [])].sort((a, b) => {
        const sortedKey = Object.entries(sortConfig).find(
          ([, value]) => value !== SortConfigTypeEnum.disabled
        )?.[0] as keyof IReportGood;

        if (!sortedKey) return 0;

        const sortedConfigElem = sortConfig?.[sortedKey];
        if (!sortedConfigElem) return 0;

        if (sortedConfigElem === SortConfigTypeEnum.desc)
          return (a[sortedKey] as number) - (b[sortedKey] as number);

        return (b[sortedKey] as number) - (a[sortedKey] as number);
      }),
    [goods, sortConfig]
  );

  const filteredGoodsByBrandsAndSellers = useMemo(() => {
    const newGoods = sortedGoods
      .filter(({ brand }) => brandsList?.includes?.(brand))
      .filter(({ seller }) => sellersList?.includes?.(seller))
      .filter((good) => {
        // Если список выбранных шильдиков равен списку всех уникальных шильдиков, показываем все товары
        if (badgesList.length === uniqueBadges.length) return true;

        // Если не выбрано ни одного шильдика, не показываем ничего
        if (badgesList.length === 0) return false;

        // Если у товара нет шильдиков, не показываем его
        if (good.badges.length === 0) return false;

        // Проверяем, есть ли у товара хотя бы один выбранный шильдик
        return good.badges.some((badge) => badgesList?.includes?.(badge));
      });

    const prices = newGoods
      .map(({ price }) => price)
      .filter((price) => price !== -1);
    if (prices.length) {
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);

      if (priceRange[0] === 0 && priceRange[1] === 0) {
        setPriceRange([minPrice, maxPrice]);
      }

      setPriceRange([minPrice, maxPrice]);

      setMinPriceRange(minPrice);
      setMaxPriceRange(maxPrice);
    } else {
      setPriceRange([0, 0]);
      setMaxPriceRange(0);
      setMinPriceRange(0);
    }

    return newGoods;
  }, [sortedGoods, brandsList, sellersList, badgesList]);

  const filteredGoodsByHavePrice = useMemo(
    () =>
      filteredGoodsByBrandsAndSellers.filter(({ price }) =>
        isHavePrice ? price !== -1 : true
      ),
    [filteredGoodsByBrandsAndSellers, isHavePrice]
  );

  const filteredGoods = useMemo(
    () =>
      filteredGoodsByHavePrice.filter(
        ({ price }) =>
          (price >= priceRange?.[0] && price <= priceRange?.[1]) || price === -1
      ),
    [filteredGoodsByHavePrice, priceRange]
  );

  const currentPageData = useMemo(
    () => filteredGoods.slice((currentPage - 1) * 10, currentPage * 10),
    [filteredGoods, currentPage]
  );

  useEffect(() => {
    setCheckboxesMap(
      currentPageData.reduce(
        (acc, value) => ({ ...acc, [value.product_id]: false }),
        {}
      )
    );
  }, [currentPageData]);

  const calcIsAllCheked = () => {
    for (const value of Object.values(checkboxesMap)) {
      if (!value) return false;
    }
    return true;
  };

  const onClickSortButton = (key: keyof IReportGood) => {
    const nextSort = (sortValue: SortConfigTypeEnum): SortConfigTypeEnum => {
      const enumValues = Object.values(SortConfigTypeEnum);
      const index = enumValues.findIndex((val) => val === sortValue);

      return (enumValues?.[index + 1] ?? enumValues[0]) as SortConfigTypeEnum;
    };

    setSortConfig((prev) => ({
      ...defaultSortConfig,
      [key]: nextSort(prev[key] as SortConfigTypeEnum),
    }));
  };

  const iconsMap = {
    [SortConfigTypeEnum.disabled]: <HiOutlineSelector size={16} />,
    [SortConfigTypeEnum.asc]: <HiChevronDoubleUp size={12} />,
    [SortConfigTypeEnum.desc]: <HiChevronDoubleDown size={12} />,
  };

  return (
    <>
      <Filters
        setFilters={setFilters}
        currentPage={CurrentPageEnum.ReportGoods}
      />
      {isLoading && (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner size="xl" />
        </div>
      )}
      {!isLoading && goods && (
        <div className="white-block mt-3 divide-y divide-gray-200 overflow-x-auto dark:divide-gray-600">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-bold text-gray-900 dark:text-gray-300">
              Популярные продукты
              <span className="ml-2 font-medium text-gray-500 dark:text-gray-400">
                {filteredGoods.length} продуктов
              </span>
            </div>
            <Dropdown
              label="Dropdown button"
              renderTrigger={() => (
                <button
                  color="white"
                  className="flex w-full items-center justify-center rounded-lg border border-gray-200 bg-white py-2 px-4 text-sm font-medium text-gray-900 hover:bg-gray-100 hover:text-primary-700 focus:z-10 focus:outline-none focus:ring-4 focus:ring-gray-200 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white dark:focus:ring-gray-700 md:w-auto"
                >
                  Фильтры
                  <svg
                    className="ml-1 h-5 w-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      clipRule="evenodd"
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    ></path>
                  </svg>
                </button>
              )}
              dismissOnClick={false}
            >
              <div className="divide-y">
                <div>
                  <LocalFilter
                    uniqueItems={uniqueBrands}
                    data={brandsList}
                    setData={setBrandsList}
                    title={'Бренд'}
                  />
                  <LocalFilter
                    uniqueItems={uniqueSellers}
                    data={sellersList}
                    setData={setSellersList}
                    title={'Продавец'}
                  />
                  <LocalFilter
                    uniqueItems={uniqueBadges}
                    data={badgesList}
                    setData={setBadgesList}
                    title={'Шильдики'}
                  />
                </div>
                <div className="p-2 text-sm">
                  <div className="flex w-full items-center justify-between p-1 font-semibold">
                    Цена
                  </div>
                  <div className="mt-2">
                    <Checkbox
                      onChange={(event) => setIsHavePrice(event.target.checked)}
                      checked={isHavePrice}
                      id={`is-have-price`}
                    />
                    <Label className="ml-1" htmlFor={`is-have-price`}>
                      Есть цена
                    </Label>
                  </div>
                  <Slider
                    min={minPriceRange}
                    max={maxPriceRange}
                    range
                    value={priceRange}
                    onChange={(value) =>
                      setPriceRange(value as [number, number])
                    }
                  />
                  <div className="flex gap-2">
                    <div className="max-w-[150px]">
                      От{' '}
                      <TextInput
                        width={50}
                        type="number"
                        onKeyDown={(e) => e.stopPropagation()}
                        value={priceRange[0]}
                        onChange={(e) => {
                          e.preventDefault();
                          setPriceRange((prev) => [+e.target.value, prev[1]]);
                        }}
                      />
                    </div>
                    <div className="max-w-[150px]">
                      До{' '}
                      <TextInput
                        width={50}
                        type="number"
                        onKeyDown={(e) => e.stopPropagation()}
                        value={priceRange[1]}
                        onChange={(e) => {
                          e.preventDefault();
                          setPriceRange((prev) => [prev[0], +e.target.value]);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Dropdown>
          </div>
          <Table className="mt-3">
            <Table.Head className="text-[10px]">
              <Table.HeadCell className="px-1 py-2 text-center">
                <Checkbox
                  checked={calcIsAllCheked()}
                  onChange={(event) => {
                    event.stopPropagation();
                    setCheckboxesMap((prev) => {
                      return Object.fromEntries(
                        Object.entries(prev).map(([key]) => [
                          key,
                          event.target.checked,
                        ])
                      );
                    });
                  }}
                  id="check-all"
                />
              </Table.HeadCell>
              <Table.HeadCell className="px-1 py-2"></Table.HeadCell>
              <Table.HeadCell className="px-1 py-2">Product</Table.HeadCell>
              <Table.HeadCell className="px-1 py-2">Бренд</Table.HeadCell>
              <Table.HeadCell className="px-1 py-2">Селлер</Table.HeadCell>
              {Object.entries(sortHeaders).map(([key, { text, tooltip }]) => (
                <Table.HeadCell className="px-1 py-2" key={key}>
                  <div className="flex items-center justify-end gap-1">
                    {text}
                    {tooltip && (
                      <HoverTooltip
                        title={tooltip}
                        text={tooltip}
                        placement="bottom"
                        className="max-w-[50%]"
                      />
                    )}
                    <button
                      onClick={() =>
                        onClickSortButton(key as keyof IReportGood)
                      }
                      className="h-[16px] w-[16px]"
                    >
                      {iconsMap?.[sortConfig?.[key] as SortConfigTypeEnum]}
                    </button>
                  </div>
                </Table.HeadCell>
              ))}
            </Table.Head>
            <Table.Body className="w-full divide-y text-xs">
              {currentPageData.map((good) => (
                <Fragment key={good.product_id}>
                  <Table.Row
                    onClick={() =>
                      setSelectedProductId(
                        good.product_id === selectedProductId
                          ? null
                          : good.product_id
                      )
                    }
                    className="cursor-pointer bg-white dark:border-gray-700 dark:bg-gray-800"
                  >
                    <Table.Cell
                      className="px-1 py-2 text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={!!checkboxesMap?.[good.product_id]}
                        onChange={(event) => {
                          event.stopPropagation();
                          setCheckboxesMap((prev) => ({
                            ...prev,
                            [good.product_id]: event.target.checked,
                          }));
                        }}
                        id="check-1"
                      />
                    </Table.Cell>
                    <Table.Cell className="px-1 py-2 text-center">
                      <svg
                        className={`m-auto h-6 w-6 shrink-0 transition-transform ${
                          good.product_id === selectedProductId
                            ? 'rotate-180'
                            : ''
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </Table.Cell>
                    <Table.Cell className="h-auto overflow-x-scroll px-1 py-2">
                      <div className="flex w-full items-center">
                        <img
                          className="h-[36px] w-[36px]"
                          src={getImageUrl(
                            filters?.platform,
                            good.product_id,
                            good.image_link
                          )}
                          alt={good.title}
                        />
                        <span
                          className={`ml-2 w-full${
                            good.product_id === selectedProductId
                              ? ''
                              : 'truncate'
                          }`}
                        >
                          {good.title}
                        </span>
                        {good?.link && (
                          <a
                            onClick={(e) => e.stopPropagation()}
                            target="_blank"
                            href={getLinkUrl(filters?.platform, good.link)}
                            // className="justify-self-end"
                            rel="noreferrer"
                          >
                            <svg
                              className="ml-1"
                              width="20"
                              height="21"
                              viewBox="0 0 20 21"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <g clipPath="url(#clip0_1804_45243)">
                                <path
                                  d="M5.52579 20.5C4.598 20.5015 3.69067 20.2024 2.91883 19.6405C2.14699 19.0787 1.54538 18.2793 1.19026 17.3439C0.835149 16.4084 0.742522 15.379 0.924122 14.386C1.10572 13.393 1.55337 12.4813 2.21033 11.7663L5.94047 7.6964C6.82047 6.73817 8.01282 6.20002 9.25593 6.20002C10.499 6.20002 11.6914 6.73817 12.5714 7.6964C12.7485 7.90251 12.8452 8.17572 12.841 8.45823C12.8367 8.74074 12.732 9.01038 12.5489 9.2101C12.3657 9.40983 12.1186 9.52396 11.8597 9.52834C11.6009 9.53273 11.3506 9.42703 11.1619 9.23361C10.6559 8.68301 9.97047 8.37383 9.25593 8.37383C8.54139 8.37383 7.856 8.68301 7.34999 9.23361L3.61985 13.3046C3.11436 13.8562 2.83038 14.6045 2.83038 15.3846C2.83038 15.7709 2.9001 16.1535 3.03556 16.5104C3.17101 16.8673 3.36956 17.1916 3.61985 17.4647C3.87014 17.7379 4.16728 17.9546 4.4943 18.1024C4.82132 18.2502 5.17182 18.3263 5.52579 18.3263C6.24066 18.3263 6.92624 18.0164 7.43173 17.4647L7.91519 16.9371C8.00807 16.8361 8.11827 16.756 8.23949 16.7015C8.36072 16.647 8.4906 16.619 8.62172 16.6193C8.75283 16.6195 8.88262 16.648 9.00367 16.703C9.12472 16.758 9.23465 16.8384 9.3272 16.9398C9.41976 17.0412 9.4931 17.1614 9.54307 17.2937C9.59303 17.426 9.61863 17.5678 9.6184 17.7109C9.61816 17.854 9.59211 17.9956 9.54172 18.1277C9.49133 18.2598 9.41759 18.3798 9.32471 18.4808L8.84125 19.0085C8.40586 19.4831 7.88883 19.8592 7.31986 20.1152C6.75089 20.3712 6.1412 20.5019 5.52579 20.5Z"
                                  fill="#1F2A37"
                                />
                                <path
                                  d="M10.7522 14.8015C10.1364 14.8026 9.52658 14.6709 8.95762 14.414C8.38866 14.1571 7.8718 13.7801 7.43671 13.3046C7.25513 13.0994 7.15466 12.8246 7.15693 12.5393C7.1592 12.2541 7.26404 11.9812 7.44886 11.7795C7.63368 11.5778 7.8837 11.4634 8.14506 11.4609C8.40643 11.4584 8.65823 11.5681 8.84623 11.7663C9.35971 12.3012 10.0423 12.5997 10.7522 12.5997C11.4621 12.5997 12.1446 12.3012 12.6581 11.7663L16.3873 7.6964C16.638 7.42353 16.837 7.09934 16.9727 6.7424C17.1085 6.38546 17.1784 6.00279 17.1784 5.61632C17.1784 5.22986 17.1085 4.84719 16.9727 4.49025C16.837 4.13331 16.638 3.80912 16.3873 3.53625C15.8741 3.00111 15.1916 2.70249 14.4818 2.70249C13.772 2.70249 13.0896 3.00111 12.5764 3.53625L11.8327 4.34783C11.6457 4.55182 11.3921 4.66636 11.1276 4.66626C10.8632 4.66616 10.6096 4.55142 10.4227 4.34728C10.2358 4.14315 10.1309 3.86634 10.1309 3.57775C10.131 3.28916 10.2362 3.01243 10.4232 2.80844L11.1669 1.99686C12.0462 1.03793 13.2385 0.49949 14.4816 0.5C15.7247 0.50051 16.9166 1.03992 17.7953 1.99958C18.6739 2.95923 19.1673 4.26051 19.1668 5.61716C19.1664 6.9738 18.6721 8.27468 17.7928 9.23361L14.0626 13.3035C13.6283 13.7786 13.1123 14.1555 12.5442 14.4125C11.9761 14.6696 11.3671 14.8018 10.7522 14.8015Z"
                                  fill="#1F2A37"
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_1804_45243">
                                  <rect
                                    width="20"
                                    height="20"
                                    fill="white"
                                    transform="translate(0 0.5)"
                                  />
                                </clipPath>
                              </defs>
                            </svg>
                          </a>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="px-1 py-2">{good.brand}</Table.Cell>
                    <Table.Cell className="px-1 py-2">{good.seller}</Table.Cell>
                    <Table.Cell className="px-1 py-2 text-right">
                      {good.impressions}
                    </Table.Cell>
                    <Table.Cell className="px-1 py-2 text-right">
                      {good.share?.toFixed?.(2)}
                    </Table.Cell>
                    <Table.Cell className="px-1 py-2 text-right">
                      {good.position?.toFixed?.(2)}
                    </Table.Cell>
                    <Table.Cell className="px-1 py-2 text-right">
                      {good.price}
                    </Table.Cell>
                    <Table.Cell className="px-1 py-2 text-right">
                      {good.rating?.toFixed?.(2)}
                    </Table.Cell>
                    <Table.Cell className="px-1 py-2 text-right">
                      {good.rating_count}
                    </Table.Cell>
                    <Table.Cell className="px-1 py-2 text-right">
                      {(good.badges || []).join(', ')}
                    </Table.Cell>
                    <Table.Cell className="px-1 py-2 text-right">
                      {good.stock}
                    </Table.Cell>
                  </Table.Row>

                  {selectedProductId == good.product_id && filters && (
                    <Table.Row className="w-full bg-white dark:border-gray-700 dark:bg-gray-800">
                      <Table.Cell className="px-1 py-2" colSpan={13}>
                        <Good
                          product_id={good.product_id}
                          start_date={filters.start_date}
                          end_date={filters.end_date}
                          keywords={filters.keywords}
                          platform={filters.platform}
                        />
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Fragment>
              ))}
            </Table.Body>
          </Table>
          <div className="flex items-center justify-between p-2 text-sm text-gray-500">
            <span className="px-2">
              Показывается{' '}
              <span className="font-bold text-gray-900 dark:text-gray-300">
                {currentPage * 10 - 9}-{currentPage * 10}
              </span>{' '}
              <span className="font-bold text-gray-900 dark:text-gray-300">
                из {filteredGoods.length} продуктов
              </span>
            </span>
            <Pagination
              simple
              defaultCurrent={currentPage}
              onChange={setCurrentPage}
              total={filteredGoods.length}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ReportGoods;
